import { HttpMethod, type IHttpClient } from '@/shared/contracts/http-client.contract';
import type { IUseCase } from '@/shared/contracts/use-case.contract';
import { UIError } from '@/shared/utils/ui-error';
import type { UseCaseInput as PlaceOrderInput } from '@/modules/orders/services/make-order.usecase';
import {
  atendimentoEndpoint,
  type AtendimentoPedidoResposta,
  type PedidoAtendimentoExtras,
} from '../types';

// Mesmo payload do `checkout/place-order` + os campos do atendimento. Sem
// agendamento; a API ignora `delivery_info` (sempre retirada).
export type PedidoAtendimentoBody = Omit<
  PlaceOrderInput,
  'store_slug' | 'schedule_date' | 'schedule_slot'
> &
  PedidoAtendimentoExtras;

// `orderId`: continua uma venda salva (PUT, reprecifica e mantém o número).
// Sem ele cria o pedido (POST).
type Input = { slug: string; pedido: PedidoAtendimentoBody; orderId?: string | null };

export class CriarPedidoAtendimentoUseCase implements IUseCase<Input, AtendimentoPedidoResposta> {
  private client: IHttpClient;

  constructor(client: IHttpClient) {
    this.client = client;
  }

  async execute({ slug, pedido, orderId }: Input): Promise<AtendimentoPedidoResposta> {
    if (!pedido.items?.length) {
      throw new UIError('ITEMS_REQUIRED', 'É obrigatório informar pelo menos um item');
    }

    // Só o modo cliente (sem `acao`) paga pelo app; no assistido a forma de
    // pagamento vem na finalização.
    if (!pedido.acao && !pedido.payment_info?.payment_methods?.length) {
      throw new UIError('PAYMENT_METHOD_REQUIRED', 'É obrigatório informar um método de pagamento');
    }

    const endpoint = orderId
      ? `${atendimentoEndpoint(slug)}/pedidos/${encodeURIComponent(orderId)}`
      : `${atendimentoEndpoint(slug)}/pedidos`;

    const response = await this.client.sendRequest<
      { data: AtendimentoPedidoResposta },
      PedidoAtendimentoBody
    >({
      endpoint,
      method: orderId ? HttpMethod.PUT : HttpMethod.POST,
      body: { ...pedido, delivery_info: { ...pedido.delivery_info, delivery_type: 2 } },
    });

    return response.data;
  }
}
